import Component from '@glimmer/component';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import t from 'ember-intl/helpers/t';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';
import AlertBanner from 'tio-ui/components/alert-banner';
import Chip from 'tio-ui/components/chip';
import Popover from 'tio-ui/components/popover';
import Table from 'tio-ui/components/table';
import { Section } from 'tio-ui/components/layout';
import { PhArrowUp, PhCaretDown, PhCaretUp, PhInfo } from 'ember-phosphor-icons';
import MultiLineChart from 'tio-ui/components/charts/multi-line-chart';
import type { MultiLineSeries } from 'tio-ui/components/charts/multi-line-chart';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import {
  generatePaymentPlanForUserRequest,
  type GeneratePaymentPlanResponse,
  type PaymentPlanRow,
} from 'tio-data/requests/payment-plan-explorer';
import {
  buildGeneratePayload,
  isCurrentPlanRow,
  scheduleMonthLabels,
  type Sf0726ResultSection,
  type SortedPlanRow,
} from '../../utils/strategy-finder-0726';
import { PlanCopyContent } from './plan-copy';

interface LoanPlansSectionSignature {
  Args: {
    section: Sf0726ResultSection;
    // The analysis date (recommendation createdAt) the schedules start from, so
    // the graph x-axis reads as Month/Year rather than a payment index.
    analysisDate?: Date | string;
  };
  Element: HTMLElement;
}

// Line colors for a plan's graph (balance solid, total paid filled, payment dashed).
const PALETTE = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2'];

const rowKey = (row: SortedPlanRow) => `${row.plan}::${row.tlf_amount}::${row.pslf}`;

export default class LoanPlansSection extends Component<LoanPlansSectionSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked scheduleByKey: Record<string, PaymentPlanRow[]> = {};
  @tracked failedKeys: string[] = [];
  // Collapsed by default: the table shows only the best and current plan; the
  // rest are revealed by the "see other plans" toggle.
  @tracked showAllPlans = false;

  // Keys with a schedule fetch in flight. `fetchSchedule` is a plain `task` (not
  // `dropTask`) on purpose: each expanded plan fetches its own schedule and they
  // must run concurrently, so dropping in-flight performs would silently starve
  // later expansions. This set instead guards only against re-fetching the SAME
  // key — e.g. a fast collapse→expand before the first fetch resolves.
  #pendingKeys = new Set<string>();

  get plans() {
    return this.args.section.plans;
  }

  dollars = (cents: number) => cents / 100;

  formatDollars = (value: number) =>
    this.intl.formatNumber(value, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  // All display text is local; the API's `title` is intentionally never rendered.
  planLabel = (row: SortedPlanRow) => {
    const key = `strategy_finder_0726.plans.${row.plan}`;
    let label = this.intl.exists(key) ? this.intl.t(key) : row.plan;
    if (row.tlf_amount > 0) {
      const amount = this.formatDollars(this.dollars(row.tlf_amount));
      label = `${label} ${this.intl.t('strategy_finder_0726.results.with_tlf', { amount })}`;
    }
    if (row.pslf) {
      // When the loan is repaid before the PSLF cutoff, PSLF forgives nothing —
      // call that out in the plan name rather than implying a benefit.
      const suffix =
        row.pslf_forgiveness > 0
          ? this.intl.t('strategy_finder_0726.results.with_pslf')
          : this.intl.t('strategy_finder_0726.results.with_pslf_nothing');
      label = `${label} ${suffix}`;
    }
    return label;
  };

  // The PSLF-forgiveness cell: the amount for a PSLF row, or an em dash for a row
  // that doesn't use PSLF (non-income-driven plans, or the current plan when the
  // borrower is eligible but not pursuing).
  pslfCell = (row: SortedPlanRow) =>
    row.pslf ? this.formatDollars(this.dollars(row.pslf_forgiveness)) : '—';

  // TLF column shows whenever the borrower opted into a TLF level; PSLF column
  // shows whenever they are PSLF-eligible. With neither, fall back to a single
  // combined "amount forgiven" column so IDR-term forgiveness still shows.
  get showTlfColumn() {
    return this.plans.some((p) => p.tlf_amount > 0);
  }

  get showPslfColumn() {
    return !!this.args.section.input?.pslf_eligible;
  }

  get showTotalForgivenessColumn() {
    return !this.showTlfColumn && !this.showPslfColumn;
  }

  // The rows are returned pre-sorted by the borrower's chosen key; seed the
  // active sort from it and let the user re-sort (ascending only — there is no
  // value in floating the highest payment to the top).
  @tracked sortColumn: 'first_month' | 'total' =
    this.args.section.sortBy === 'total_then_first_month' ? 'total' : 'first_month';

  setSortColumn = (column: 'first_month' | 'total') => {
    this.sortColumn = column;
  };

  isSortedBy = (column: 'first_month' | 'total') => this.sortColumn === column;

  get sortedPlans() {
    const rows = [...this.plans];
    if (this.sortColumn === 'total') {
      rows.sort(
        (a, b) => a.total_payment - b.total_payment || a.first_month_payment - b.first_month_payment
      );
    } else {
      rows.sort(
        (a, b) => a.first_month_payment - b.first_month_payment || a.total_payment - b.total_payment
      );
    }
    return rows;
  }

  toggleShowAllPlans = () => {
    this.showAllPlans = !this.showAllPlans;
  };

  // The recommended best plan for this loan (the top of the borrower's chosen
  // sort), matching the "best plan" column in the comparison table above.
  get bestRow() {
    return this.args.section.plans[0];
  }

  get currentRow() {
    return this.plans.find((row) => this.isCurrentPlan(row));
  }

  // The collapsed view shows just the best and current plan rows (deduped — the
  // current plan is often also the best, or there may be no current plan).
  get collapsedPlans() {
    const seen = new Set<string>();
    const rows: SortedPlanRow[] = [];
    for (const row of [this.bestRow, this.currentRow]) {
      if (row && !seen.has(rowKey(row))) {
        seen.add(rowKey(row));
        rows.push(row);
      }
    }
    return rows;
  }

  // Rows actually rendered in the table: every plan when expanded, otherwise
  // just the best + current plan.
  get displayedPlans() {
    return this.showAllPlans ? this.sortedPlans : this.collapsedPlans;
  }

  // Whether there are more plans than the collapsed view shows (so the
  // expand/collapse toggle is worth rendering).
  get hasOtherPlans() {
    return this.sortedPlans.length > this.collapsedPlans.length;
  }

  isCurrentPlan = (row: SortedPlanRow) => isCurrentPlanRow(row, this.args.section);

  // The recommended best plan (the top of the borrower's chosen sort), matched
  // by row key so the right variant is tagged when a plan appears more than once.
  isBestPlan = (row: SortedPlanRow) => {
    const best = this.bestRow;
    return !!best && rowKey(row) === rowKey(best);
  };

  // Unique per-row test key (plan + TLF + PSLF variant), since the same plan can
  // appear in multiple rows — `data-test-sf-0726-plan-row` (plan only) is ambiguous.
  rowKeyFor = (row: SortedPlanRow) => rowKey(row);

  rowClass = (row: SortedPlanRow) => (this.isCurrentPlan(row) ? 'tio-bg-success-soft' : '');

  fetchSchedule = task(async (row: SortedPlanRow) => {
    const input = this.args.section.input;
    if (!input) {
      return;
    }
    const key = rowKey(row);
    this.#pendingKeys.add(key);
    this.failedKeys = this.failedKeys.filter((k) => k !== key);
    try {
      const payload = buildGeneratePayload(input, row.plan, row.tlf_amount, row.pslf);
      const { content } = await this.store.request(generatePaymentPlanForUserRequest(payload));
      const data = content as GeneratePaymentPlanResponse;
      if (data.is_error || data.error || !Array.isArray(data.rows) || data.rows.length === 0) {
        // Empty rows would be silently filtered out of the chart, leaving the
        // user with no feedback — surface it as an error instead.
        throw new Error(data.error ?? 'generate returned no schedule');
      }
      this.scheduleByKey = { ...this.scheduleByKey, [key]: data.rows };
    } catch (e) {
      console.error(e);
      this.failedKeys = [...this.failedKeys, key];
    } finally {
      this.#pendingKeys.delete(key);
    }
  });

  // Month/Year x-axis labels starting from the analysis date, one per month.
  monthLabels = (count: number): string[] =>
    scheduleMonthLabels(this.args.analysisDate, count, (date) =>
      this.intl.formatDate(date, { month: 'short', year: 'numeric', timeZone: 'UTC' })
    );

  // Build one plan's graph series (balance, cumulative total paid, monthly payment)
  // from its fetched schedule. Each plan's graph is standalone now (shown inside its
  // own expanded row), so the axes auto-scale rather than sharing a comparison range.
  // Returns null until the schedule has loaded.
  chartForRow = (row: SortedPlanRow): { labels: string[]; series: MultiLineSeries[] } | null => {
    const rows = this.scheduleByKey[rowKey(row)];
    if (!rows?.length) {
      return null;
    }
    const balance = rows.map((r) => r.balance / 100);
    const monthly = rows.map((r) => r.payment / 100);
    // Running cumulative total paid (employee + employer), like the explorer.
    let cumulativeTotal = 0;
    const totalPaid = rows.map((r) => {
      cumulativeTotal += (r.payment + r.employer_payment) / 100;
      return cumulativeTotal;
    });
    return {
      labels: this.monthLabels(rows.length),
      series: [
        {
          label: this.intl.t('strategy_finder_0726.graph.balance'),
          data: balance,
          color: PALETTE[0] ?? '#2563eb',
        },
        {
          label: this.intl.t('strategy_finder_0726.graph.total_paid'),
          data: totalPaid,
          color: PALETTE[2] ?? '#d97706',
          filled: true,
        },
        {
          label: this.intl.t('strategy_finder_0726.graph.monthly_payment'),
          data: monthly,
          color: PALETTE[1] ?? '#16a34a',
          dashed: true,
          yAxis: 'right',
        },
      ],
    };
  };

  // A plan's schedule fetch failed (used to show an inline error in its row).
  isChartFailed = (row: SortedPlanRow) => this.failedKeys.includes(rowKey(row));

  // A graph can only be drawn when the section carries the calc input the schedule
  // fetch needs; without it, skip the graph entirely rather than spinning forever.
  get canGraph() {
    return !!this.args.section.input;
  }

  // This loan's TLF forgiveness is a split share of the household amount (only
  // when its balance share multiplier is < 1, i.e. it shares the Direct/FFEL
  // pool with another loan type and TLF is in play).
  get showTlfSplitInfo() {
    const multiplier = this.args.section.input?.multiplier ?? 1;
    return multiplier < 1 && this.plans.some((p) => p.tlf_amount > 0);
  }

  // Per-row "Plan details" disclosure state, lifted here so the expanded copy can
  // render in its own full-width row (colspan) below the plan rather than being
  // boxed into the narrow Plan column. Tracked via a re-assigned array (matching
  // selectedRows) since Set mutations are not reactive.
  @tracked openCopyKeys: string[] = [];

  isCopyOpen = (row: SortedPlanRow) => this.openCopyKeys.includes(rowKey(row));

  toggleCopy = (row: SortedPlanRow) => {
    const key = rowKey(row);
    if (this.isCopyOpen(row)) {
      this.openCopyKeys = this.openCopyKeys.filter((k) => k !== key);
    } else {
      this.openCopyKeys = [...this.openCopyKeys, key];
      // Fetch this plan's schedule the first time its details open, to draw its
      // graph — but only when a graph is possible (the section has calc input).
      if (this.canGraph && !this.scheduleByKey[key] && !this.#pendingKeys.has(key)) {
        this.fetchSchedule.perform(row);
      }
    }
  };

  // Stable id wiring the toggle's aria-controls to its full-width copy row, so
  // assistive tech associates the disclosure button with the content it reveals.
  // Sanitized because rowKey contains "::" / "true" segments.
  copyRowId = (row: SortedPlanRow) => `sf-0726-copy-${rowKey(row).replace(/[^a-zA-Z0-9]/g, '-')}`;

  // Number of columns the full-width copy row must span: the five fixed columns
  // (plan, first/highest/total payment, number of payments) plus whichever
  // forgiveness columns are shown.
  get columnCount() {
    let count = 5;
    if (this.showTlfColumn) {
      count += 1;
    }
    if (this.showPslfColumn) {
      count += 1;
    }
    if (this.showTotalForgivenessColumn) {
      count += 1;
    }
    return count;
  }

  <template>
    <Section data-test-sf-0726-results-section={{@section.loanType}} ...attributes>
      <:header>{{this.loanTypeLabel @section.loanType}}</:header>
      <:body>
        {{#if @section.availablePlansWarning}}
          <AlertBanner @intent="warning" class="mb-3">
            <p class="text-sm">{{t "strategy_finder_0726.results.available_plans_warning"}}</p>
          </AlertBanner>
        {{/if}}

        <Table @isLoading={{false}}>
          <:thead as |options|>
            <options.tr>
              <options.th>{{t "strategy_finder_0726.results.columns.plan"}}</options.th>
              <options.th>
                <button
                  type="button"
                  class="tio-row items-center gap-1"
                  data-test-sf-0726-sort="first_month"
                  {{on "click" (fn this.setSortColumn "first_month")}}
                >
                  {{t "strategy_finder_0726.results.columns.first_month_payment"}}
                  {{#if (this.isSortedBy "first_month")}}
                    <PhArrowUp @size={{14}} data-test-sf-0726-sort-active />
                  {{/if}}
                </button>
              </options.th>
              <options.th>{{t
                  "strategy_finder_0726.results.columns.max_monthly_payment"
                }}</options.th>
              <options.th>
                <button
                  type="button"
                  class="tio-row items-center gap-1"
                  data-test-sf-0726-sort="total"
                  {{on "click" (fn this.setSortColumn "total")}}
                >
                  {{t "strategy_finder_0726.results.columns.total_payment"}}
                  {{#if (this.isSortedBy "total")}}
                    <PhArrowUp @size={{14}} data-test-sf-0726-sort-active />
                  {{/if}}
                </button>
              </options.th>
              {{#if this.showTlfColumn}}
                <options.th>
                  <span class="tio-row items-center gap-1">
                    {{t "strategy_finder_0726.results.columns.tlf_forgiveness"}}
                    {{#if this.showTlfSplitInfo}}
                      <Popover @triggerMode="hover">
                        <:trigger as |pop|>
                          <span
                            {{pop.register}}
                            {{pop.hoverHandlers}}
                            data-test-sf-0726-tlf-split-info
                          >
                            <PhInfo class="tio-fg-secondary" @size={{14}} />
                          </span>
                        </:trigger>
                        <:body>
                          <p class="text-sm max-w-xs">{{t
                              "strategy_finder_0726.results.tlf_split_info"
                            }}</p>
                        </:body>
                      </Popover>
                    {{/if}}
                  </span>
                </options.th>
              {{/if}}
              {{#if this.showPslfColumn}}
                <options.th>{{t
                    "strategy_finder_0726.results.columns.pslf_forgiveness"
                  }}</options.th>
              {{/if}}
              {{#if this.showTotalForgivenessColumn}}
                <options.th>{{t "strategy_finder_0726.results.columns.forgiveness"}}</options.th>
              {{/if}}
              <options.th>{{t "strategy_finder_0726.results.columns.num_payments"}}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            {{#each this.displayedPlans as |row|}}
              <options.tr
                class="align-top {{this.rowClass row}}"
                data-test-sf-0726-plan-row={{row.plan}}
                data-test-sf-0726-plan-row-key={{this.rowKeyFor row}}
                data-test-sf-0726-current-plan={{this.isCurrentPlan row}}
              >
                <options.td>
                  {{this.planLabel row}}
                  {{#if (this.isBestPlan row)}}
                    <Chip @size="sm" @intent="primary" data-test-sf-0726-best-plan-badge>
                      {{t "strategy_finder_0726.results.best_plan_badge"}}
                    </Chip>
                  {{/if}}
                  {{#if (this.isCurrentPlan row)}}
                    <Chip @size="sm" data-test-sf-0726-current-plan-badge>
                      {{t "strategy_finder_0726.results.current_plan_badge"}}
                    </Chip>
                  {{/if}}
                  {{! Toggle stays in the Plan column; the copy it reveals renders
                      in a full-width row below (see the copy row after this tr). }}
                  <button
                    type="button"
                    class="tio-row items-center gap-1 tio-fg-brand text-sm mt-1"
                    aria-expanded={{if (this.isCopyOpen row) "true" "false"}}
                    aria-controls={{this.copyRowId row}}
                    data-test-sf-0726-row-copy={{this.rowKeyFor row}}
                    {{on "click" (fn this.toggleCopy row)}}
                  >
                    {{t "strategy_finder_0726.plan_copy.details_toggle"}}
                    {{#if (this.isCopyOpen row)}}
                      <PhCaretUp @size={{14}} />
                    {{else}}
                      <PhCaretDown @size={{14}} />
                    {{/if}}
                  </button>
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.first_month_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.max_monthly_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars row.total_payment)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                {{#if this.showTlfColumn}}
                  <options.td data-test-sf-0726-tlf-cell>
                    {{safeFormatNumber
                      (this.dollars row.tlf_forgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                {{/if}}
                {{#if this.showPslfColumn}}
                  <options.td data-test-sf-0726-pslf-cell>{{this.pslfCell row}}</options.td>
                {{/if}}
                {{#if this.showTotalForgivenessColumn}}
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars row.forgiveness_paid)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                {{/if}}
                <options.td>{{row.num_payments}}</options.td>
              </options.tr>
              {{! Full-width copy row. Kept in the DOM (hidden when collapsed) so the
                  toggle's aria-controls always references a real element. }}
              <options.tr
                data-test-sf-0726-row-copy-panel={{this.rowKeyFor row}}
                hidden={{unless (this.isCopyOpen row) true}}
              >
                <options.td colspan="{{this.columnCount}}" id={{this.copyRowId row}}>
                  {{#if (this.isCopyOpen row)}}
                    {{#if this.canGraph}}
                      {{! Graph first (right under the plan summary line), then the copy.
                          Rendered only while open so the canvas chart mounts at full width. }}
                      {{#let (this.chartForRow row) as |chart|}}
                        <div class="mb-4" data-test-sf-0726-graph-plan={{this.rowKeyFor row}}>
                          {{#if chart}}
                            <MultiLineChart
                              @labels={{chart.labels}}
                              @series={{chart.series}}
                              @formatValue={{this.formatDollars}}
                              @formatValueRight={{this.formatDollars}}
                              @height={{240}}
                            />
                          {{else if (this.isChartFailed row)}}
                            <p class="text-sm tio-fg-danger">{{t
                                "strategy_finder_0726.graph.error"
                              }}</p>
                          {{else}}
                            <p class="text-sm tio-fg-secondary">{{t
                                "strategy_finder_0726.graph.loading"
                              }}</p>
                          {{/if}}
                        </div>
                      {{/let}}
                    {{/if}}
                    <PlanCopyContent
                      @plan={{row.plan}}
                      @pslf={{row.pslf}}
                      @pslfForgiveness={{row.pslf_forgiveness}}
                      @tlfAmount={{row.tlf_amount}}
                    />
                  {{/if}}
                </options.td>
              </options.tr>
            {{/each}}
          </:tbody>
        </Table>

        {{#if this.hasOtherPlans}}
          <button
            type="button"
            class="tio-row items-center gap-1 tio-fg-brand text-sm mt-3"
            aria-expanded={{if this.showAllPlans "true" "false"}}
            data-test-sf-0726-toggle-other-plans
            {{on "click" this.toggleShowAllPlans}}
          >
            {{#if this.showAllPlans}}
              {{t "strategy_finder_0726.results.show_fewer_plans"}}
              <PhCaretUp @size={{14}} />
            {{else}}
              {{t "strategy_finder_0726.results.see_other_plans"}}
              <PhCaretDown @size={{14}} />
            {{/if}}
          </button>
        {{/if}}
      </:body>
    </Section>
  </template>
}
